"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateApplicantNameData = exports.applicantNameFormRules = exports.applicantNameFieldMapping = exports.applicantNameSchema = exports.APPLICANT_NAME_PATTERN = void 0;
const converters_1 = require("../converters");
// Maximum length for a name segment, aligned with the sibling applicant name fields
// (applicant_name_first/middle/last) and the address manager_*_name fields (all 50).
const NAME_MAX_LENGTH = 50;
// Shared name-character semantics for applicant name fields. This mirrors the
// project's existing name validation (see @common/ui FIELD_VALIDATION used by
// applicant_name_first/middle/last): a blocklist that rejects problematic special
// characters while allowing all letters (incl. Unicode such as José, Müller, 张伟,
// محمد), digits, spaces, apostrophes (straight and curly), hyphens and periods so
// real surnames like "St. John" and "O'Brien" are accepted. The pattern is
// ES5-compatible (no \p{L}/u flag) so it also compiles under the viewer's ES5 target.
//
// Kept in sync with @common/ui `NAME_PATTERN`. Centralized here so the UI and
// backend validate applicant names identically.
exports.APPLICANT_NAME_PATTERN = /^[^@#$%^&*()_+=[\]{}|\\<>?/~`!;:"]+$/;
// Every free-text applicant name field that the Leasing Journey applicant save
// forwards to Core shares the same semantics (max length + blocked characters),
// so they all reference the single rule/message pair below. Listing the keys once
// keeps the backend request-boundary validator (validateApplicantNameData) and the
// viewer's React Hook Form rules in lockstep without repeating the rule per field.
const APPLICANT_NAME_FIELD_KEYS = [
    'applicant_name_maternal_last',
    'applicant_former_name_last',
    'alternate_first_name',
    'alternate_middle_name',
    'alternate_last_name',
    'applicant_name_preferred',
    // Legacy standalone "Alternate Name" field still persisted in forms published
    // before the AlternateName block migration (DEV-311888). It is the fallback
    // source for alt_name_first at the API boundary, so it is validated too.
    'applicant_name_alternate',
];
const applicantNameRule = {
    maxLength: NAME_MAX_LENGTH,
    pattern: exports.APPLICANT_NAME_PATTERN,
};
const applicantNameMessages = {
    maxLength: `validation.maxLength|${NAME_MAX_LENGTH}`,
    pattern: 'validation.invalidName',
};
/**
 * Validation schema for the free-text applicant name fields introduced for the
 * Leasing Journey (maternal last, former/maiden, alternate first/middle/last,
 * preferred, and the legacy alternate name). Intentionally omits `required` so
 * each field honors its Form Builder Required/Optional configuration, which is
 * applied separately by the consuming UI (FieldTypeAdaptor in the viewer).
 */
exports.applicantNameSchema = {
    fields: APPLICANT_NAME_FIELD_KEYS.reduce((fields, fieldName) => {
        fields[fieldName] = { ...applicantNameRule };
        return fields;
    }, {}),
    messages: APPLICANT_NAME_FIELD_KEYS.reduce((messages, fieldName) => {
        messages[fieldName] = { ...applicantNameMessages };
        return messages;
    }, {}),
};
/**
 * Field mapping (backend field name -> UI field name). These applicant name
 * fields use the same key on both sides, so each mapping is identity.
 */
exports.applicantNameFieldMapping = APPLICANT_NAME_FIELD_KEYS.reduce((mapping, fieldName) => {
    mapping[fieldName] = fieldName;
    return mapping;
}, {});
/**
 * Pre-computed React Hook Form rules for applicant name fields. Consumed by the
 * form viewer's VALIDATION map so the maternal last name shares a single source
 * of truth with any other consumer (and the backend validator below).
 */
exports.applicantNameFormRules = (0, converters_1.toReactHookForm)(exports.applicantNameSchema, exports.applicantNameFieldMapping);
/**
 * Runtime validator for applicant name fields (for backend/non-RHF consumers).
 */
exports.validateApplicantNameData = (0, converters_1.createValidator)(exports.applicantNameSchema);
